import { useContext, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";
import { AmplitudeContext } from "./AmplitudeProvider";
import {
  AMPLITUDE_USER_TYPES,
  buildAffiliationIdentifyProperties,
  resolveAmplitudeUserType,
  setAffiliationIdentifyProperties
} from "./affiliationIdentify";
import { AMPLITUDE_AFFILIATION_SECTIONS_QUERY, AMPLITUDE_AFFILIATION_UNIVERSITIES_QUERY } from "./queries";
import useCapabilities from "@/hooks/useCapabilities";
import { useAssumeUser } from "@/providers/AssumeUserProvider";

function applyAffiliationIdentify(
  amplitudeClient: typeof import("@amplitude/analytics-browser"),
  properties: Parameters<typeof setAffiliationIdentifyProperties>[1]
) {
  const identify = new amplitudeClient.Identify();
  setAffiliationIdentifyProperties(identify, properties);
  amplitudeClient.identify(identify);
}

const AmplitudeAffiliationIdentify = () => {
  const amplitude = useContext(AmplitudeContext);
  const { assumedUser } = useAssumeUser();
  const {
    loaded,
    roles = [],
    universityLegacyIds = [],
    sectionLegacyIds = [],
    sectionLegacyIdsLoading
  } = useCapabilities();
  const lastSentProperties = useRef<string | null>(null);

  const userType = useMemo(() => resolveAmplitudeUserType(roles), [roles]);
  const isSectionStaff = userType === AMPLITUDE_USER_TYPES.SECTION_STAFF;
  const isUniversityAdmin = userType === AMPLITUDE_USER_TYPES.UNIVERSITY_ADMIN;
  const skipBecauseImpersonating = Boolean(assumedUser);

  const {
    data: sectionsData,
    loading: sectionsLoading,
    error: sectionsError
  } = useQuery(AMPLITUDE_AFFILIATION_SECTIONS_QUERY, {
    skip: !loaded || skipBecauseImpersonating || !isSectionStaff
  });

  const {
    data: universitiesData,
    loading: universitiesLoading,
    error: universitiesError
  } = useQuery(AMPLITUDE_AFFILIATION_UNIVERSITIES_QUERY, {
    skip: !loaded || skipBecauseImpersonating || !isUniversityAdmin || universityLegacyIds.length === 0,
    variables: { legacyId: universityLegacyIds[0] }
  });

  const catalogLookupsSettled =
    loaded &&
    !skipBecauseImpersonating &&
    (!isSectionStaff || (!sectionLegacyIdsLoading && !sectionsLoading)) &&
    (!isUniversityAdmin || universityLegacyIds.length === 0 || !universitiesLoading);
  const catalogFailed = Boolean(sectionsError || universitiesError);

  useEffect(() => {
    if (!catalogLookupsSettled) {
      return;
    }

    const properties = buildAffiliationIdentifyProperties({
      roles,
      sectionLegacyIds,
      universityLegacyIds,
      sections: catalogFailed ? undefined : sectionsData?.sections,
      universities: catalogFailed ? undefined : universitiesData?.universities
    });
    const serializedProperties = JSON.stringify(properties);
    if (lastSentProperties.current === serializedProperties) {
      return;
    }

    applyAffiliationIdentify(amplitude, properties);
    lastSentProperties.current = serializedProperties;
  }, [
    catalogFailed,
    catalogLookupsSettled,
    amplitude,
    roles,
    sectionLegacyIds,
    sectionsData?.sections,
    universitiesData?.universities,
    universityLegacyIds
  ]);

  return null;
};

export default AmplitudeAffiliationIdentify;
